import React, { useState, useEffect, useCallback, useMemo } from "react";
import "@/App.css";
import "@/LeadCalculator.css";
import axios from "axios";
import LeadCalculator from "./LeadCalculator";
import { 
  Calculator, BarChart3, GitBranch, Clock, BookOpen, 
  FileText, Ship, Shield, Building2, Stamp, Percent,
  Briefcase, Truck, Package, Warehouse, MoreHorizontal,
  Globe, ChevronDown, ChevronUp, DollarSign, TrendingUp,
  AlertTriangle, CheckCircle, Target, RefreshCw, Tag,
  Box, PieChart, Search, X, Ban, Check, MessageCircle,
  User, Mail, Phone, ExternalLink, TrendingDown, ArrowRight,
  Zap, ChevronRight
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WHATSAPP_NUMBER = "+8616628526361";

// WhatsApp CTA Component - Simplified
const WhatsAppCTA = ({ message, buttonText, className = "" }) => {
  const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(message)}`;
  
  return (
    <a 
      href={whatsappUrl} 
      target="_blank" 
      rel="noopener noreferrer" 
      className={`whatsapp-cta ${className}`}
      data-testid="whatsapp-cta"
    >
      <MessageCircle size={16} />
      <span>{buttonText}</span>
    </a>
  );
};

// Mini CTA - More compact version
const MiniCTA = ({ icon: Icon, text, ctaText, whatsappMessage }) => (
  <div className="mini-cta">
    <div className="mini-cta-content">
      <Icon size={18} />
      <span>{text}</span>
    </div>
    <WhatsAppCTA message={whatsappMessage} buttonText={ctaText} className="mini-cta-btn" />
  </div>
);

// Format currency
const formatCurrency = (amount, currency = "MXN") => {
  return new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: currency === "MXN" ? "MXN" : "USD",
    minimumFractionDigits: 2,
  }).format(amount).replace("MX$", "$");
};

// Format currency short (no decimals for display)
const formatCurrencyShort = (amount) => {
  return new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount).replace("MX$", "$");
};

// Icon mapping
const iconMap = {
  document: FileText,
  ship: Ship,
  shield: Shield,
  bank: Building2,
  stamp: Stamp,
  percent: Percent,
  briefcase: Briefcase,
  truck: Truck,
  package: Package,
  warehouse: Warehouse,
  ellipsis: MoreHorizontal,
  globe: Globe,
};

// Live Cost Preview Component
const LiveCostPreview = ({ formData, exchangeRate }) => {
  const preview = useMemo(() => {
    const imp = formData.import_costs;
    const add = formData.additional_costs;
    
    if (!imp.commercial_invoice_value || imp.commercial_invoice_value <= 0) {
      return null;
    }
    
    const invoiceMxn = imp.commercial_invoice_value * exchangeRate;
    const freightMxn = imp.international_freight * exchangeRate;
    const customsValue = invoiceMxn + freightMxn;
    
    const igi = customsValue * (imp.igi_rate || 0);
    const ige = customsValue * (imp.ige_rate || 0);
    const dta = customsValue * imp.dta_rate;
    const ivaBase = customsValue + igi + dta;
    const iva = ivaBase * imp.iva_rate;
    
    const importSubtotal = invoiceMxn + freightMxn + igi + ige + dta + iva;
    
    const servicesBase = (add.customs_broker_fee || 0) + (add.bl_revalidation || 0) + 
                        (add.handling || 0) + (add.storage || 0) + (add.inland_freight || 0);
    const ivaServices = servicesBase * 0.16;
    const additionalSubtotal = servicesBase + ivaServices + (add.insurance || 0) + 
                               (add.extra_cost_1 || 0) + (add.extra_cost_2 || 0);
    
    const total = importSubtotal + additionalSubtotal;
    const units = imp.units || 1;
    const perUnit = total / units;
    
    return {
      invoiceMxn,
      igi,
      iva: iva + ivaServices,
      logistics: freightMxn + additionalSubtotal,
      total,
      perUnit,
      units
    };
  }, [formData, exchangeRate]);

  if (!preview) {
    return (
      <div 
        style={{
          background: 'white',
          border: '2px dashed #e5e7eb',
          borderRadius: '16px',
          overflow: 'hidden'
        }}
      >
        <div style={{ padding: '48px 24px', textAlign: 'center', color: '#9ca3af' }}>
          <Calculator size={32} style={{ marginBottom: '12px', opacity: 0.5 }} />
          <p style={{ fontSize: '14px', margin: 0 }}>Ingresa el valor de tu mercancía para ver el costo estimado</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      style={{
        background: 'white',
        border: '2px solid #e5e7eb',
        borderRadius: '16px',
        overflow: 'hidden',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
      }}
      data-testid="live-preview"
    >
      <div style={{
        background: '#1e3a5f',
        color: 'white',
        padding: '12px 20px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontWeight: '600'
      }}>
        <Zap size={18} style={{ color: '#d97706' }} />
        <span>Costo Estimado</span>
      </div>
      <div style={{
        padding: '24px 20px',
        background: 'linear-gradient(135deg, #1e3a5f 0%, #152a47 100%)',
        color: 'white',
        textAlign: 'center'
      }}>
        <span style={{ display: 'block', fontSize: '12px', opacity: 0.8, marginBottom: '4px' }}>Total Importación</span>
        <span style={{ display: 'block', fontSize: '36px', fontWeight: '700', letterSpacing: '-1px' }}>{formatCurrencyShort(preview.total)}</span>
        <span style={{ display: 'block', fontSize: '13px', opacity: 0.85, marginTop: '4px' }}>
          {formatCurrencyShort(preview.perUnit)} / unidad × {preview.units}
        </span>
      </div>
      <div style={{ padding: '16px 20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #e5e7eb', fontSize: '14px' }}>
          <span style={{ color: '#6b7280' }}>Valor Mercancía</span>
          <span style={{ fontWeight: '600', color: '#1f2937' }}>{formatCurrencyShort(preview.invoiceMxn)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #e5e7eb', fontSize: '14px' }}>
          <span style={{ color: '#6b7280' }}>IGI (Arancel)</span>
          <span style={{ fontWeight: '600', color: '#1f2937' }}>{formatCurrencyShort(preview.igi)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #e5e7eb', fontSize: '14px' }}>
          <span style={{ color: '#6b7280' }}>IVA Total</span>
          <span style={{ fontWeight: '600', color: '#1f2937' }}>{formatCurrencyShort(preview.iva)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', fontSize: '14px' }}>
          <span style={{ color: '#6b7280' }}>Logística</span>
          <span style={{ fontWeight: '600', color: '#1f2937' }}>{formatCurrencyShort(preview.logistics)}</span>
        </div>
      </div>
    </div>
  );
};

// Compact Banxico Reference for Import Details
const BanxicoRateInline = ({ banxicoRate, onUseRate }) => {
  if (!banxicoRate) return null;
  
  return (
    <div className="banxico-inline" data-testid="banxico-inline">
      <div className="banxico-inline-info">
        <span className="banxico-inline-label">Banxico FIX:</span>
        <span className="banxico-inline-rate">{banxicoRate.rate?.toFixed(4) || "---"}</span>
        <span className="banxico-inline-date">({banxicoRate.date})</span>
      </div>
      <button 
        className="banxico-inline-btn"
        onClick={onUseRate}
        disabled={!banxicoRate?.rate}
        data-testid="use-banxico-inline"
      >
        Usar
      </button>
      <a 
        href="https://www.banxico.org.mx/tipcamb/tipCamMIAction.do?idioma=sp" 
        target="_blank" 
        rel="noopener noreferrer"
        className="banxico-inline-link"
      >
        <ExternalLink size={12} />
      </a>
    </div>
  );
};

// Leads Dashboard Component with Analytics
const LeadsDashboard = ({ leads, onRefresh, loading }) => {
  const [dateFilter, setDateFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [activeView, setActiveView] = useState("analytics"); // analytics | table

  // Calculate analytics
  const analytics = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekAgo = new Date(now - 7 * 24 * 60 * 60 * 1000);
    const monthAgo = new Date(now - 30 * 24 * 60 * 60 * 1000);

    // Leads by time period
    const leadsToday = leads.filter(l => new Date(l.created_at) >= today).length;
    const leadsThisWeek = leads.filter(l => new Date(l.created_at) >= weekAgo).length;
    const leadsThisMonth = leads.filter(l => new Date(l.created_at) >= monthAgo).length;

    // Leads by day (last 7 days)
    const leadsByDay = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date(now);
      date.setDate(date.getDate() - i);
      const dayStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      const dayEnd = new Date(dayStart.getTime() + 24 * 60 * 60 * 1000);
      const count = leads.filter(l => {
        const d = new Date(l.created_at);
        return d >= dayStart && d < dayEnd;
      }).length;
      leadsByDay.push({
        day: dayStart.toLocaleDateString('es-MX', { weekday: 'short', day: 'numeric' }),
        count
      });
    }

    // Total estimated value
    const totalValue = leads.reduce((sum, l) => {
      const val = parseFloat(l.productValue) || 0;
      return sum + val;
    }, 0);

    // Average value per lead
    const avgValue = leads.length > 0 ? totalValue / leads.length : 0;

    // Top tariff codes
    const tariffCounts = {};
    leads.forEach(l => {
      if (l.tariff) {
        const chapter = l.tariff.substring(0, 4);
        tariffCounts[chapter] = (tariffCounts[chapter] || 0) + 1;
      }
    });
    const topTariffs = Object.entries(tariffCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);

    // Conversion rate (leads with all info vs partial)
    const completeLeads = leads.filter(l => l.name && l.email && l.phone).length;
    const conversionRate = leads.length > 0 ? (completeLeads / leads.length * 100).toFixed(1) : 0;

    return {
      leadsToday,
      leadsThisWeek,
      leadsThisMonth,
      leadsByDay,
      totalValue,
      avgValue,
      topTariffs,
      conversionRate,
      completeLeads
    };
  }, [leads]);

  const filteredLeads = leads.filter(lead => {
    // Date filter
    if (dateFilter !== "all") {
      const leadDate = new Date(lead.created_at);
      const now = new Date();
      if (dateFilter === "today") {
        if (leadDate.toDateString() !== now.toDateString()) return false;
      } else if (dateFilter === "week") {
        const weekAgo = new Date(now - 7 * 24 * 60 * 60 * 1000);
        if (leadDate < weekAgo) return false;
      } else if (dateFilter === "month") {
        const monthAgo = new Date(now - 30 * 24 * 60 * 60 * 1000);
        if (leadDate < monthAgo) return false;
      }
    }
    
    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return (
        lead.name?.toLowerCase().includes(term) ||
        lead.email?.toLowerCase().includes(term) ||
        lead.phone?.toLowerCase().includes(term)
      );
    }
    
    return true;
  });

  const exportToExcel = () => {
    const headers = ["Nombre", "Email", "Teléfono", "Empresa", "Fracción", "Valor USD", "Fecha"];
    const rows = filteredLeads.map(lead => [
      lead.name || "",
      lead.email || "",
      lead.phone || "",
      lead.company || "",
      lead.tariff || "",
      lead.productValue || "",
      new Date(lead.created_at).toLocaleString("es-MX")
    ]);
    
    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(","))
    ].join("\n");
    
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `leads_${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
  };

  // Find max for chart scaling
  const maxDayCount = Math.max(...analytics.leadsByDay.map(d => d.count), 1);

  return (
    <div className="leads-dashboard" data-testid="leads-dashboard">
      <div className="leads-header">
        <h2>Dashboard de Leads</h2>
        <p className="leads-subtitle">Analytics y seguimiento de prospectos</p>
      </div>

      {/* View Toggle */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
        <button
          onClick={() => setActiveView('analytics')}
          style={{
            padding: '10px 20px',
            border: 'none',
            borderRadius: '8px',
            background: activeView === 'analytics' ? '#1e3a5f' : '#e5e7eb',
            color: activeView === 'analytics' ? 'white' : '#4b5563',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}
        >
          <BarChart3 size={16} /> Analytics
        </button>
        <button
          onClick={() => setActiveView('table')}
          style={{
            padding: '10px 20px',
            border: 'none',
            borderRadius: '8px',
            background: activeView === 'table' ? '#1e3a5f' : '#e5e7eb',
            color: activeView === 'table' ? 'white' : '#4b5563',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}
        >
          <User size={16} /> Lista de Leads
        </button>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
          <button className="refresh-btn" onClick={onRefresh} disabled={loading} style={{
            padding: '10px 16px',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            background: 'white',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}>
            <RefreshCw size={16} className={loading ? "spin" : ""} />
          </button>
          <button className="export-btn" onClick={exportToExcel} disabled={filteredLeads.length === 0} style={{
            padding: '10px 16px',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            background: 'white',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}>
            <FileText size={16} /> CSV
          </button>
        </div>
      </div>

      {activeView === 'analytics' ? (
        <>
          {/* Main Stats Cards */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '16px',
            marginBottom: '24px'
          }}>
            <div style={{
              background: 'linear-gradient(135deg, #1e3a5f 0%, #152a47 100%)',
              borderRadius: '12px',
              padding: '20px',
              color: 'white'
            }}>
              <div style={{ fontSize: '32px', fontWeight: '700' }}>{leads.length}</div>
              <div style={{ fontSize: '14px', opacity: 0.85 }}>Total Leads</div>
            </div>
            <div style={{
              background: 'white',
              border: '2px solid #e5e7eb',
              borderRadius: '12px',
              padding: '20px'
            }}>
              <div style={{ fontSize: '32px', fontWeight: '700', color: '#059669' }}>{analytics.leadsToday}</div>
              <div style={{ fontSize: '14px', color: '#6b7280' }}>Hoy</div>
            </div>
            <div style={{
              background: 'white',
              border: '2px solid #e5e7eb',
              borderRadius: '12px',
              padding: '20px'
            }}>
              <div style={{ fontSize: '32px', fontWeight: '700', color: '#d97706' }}>{analytics.leadsThisWeek}</div>
              <div style={{ fontSize: '14px', color: '#6b7280' }}>Esta Semana</div>
            </div>
            <div style={{
              background: 'white',
              border: '2px solid #e5e7eb',
              borderRadius: '12px',
              padding: '20px'
            }}>
              <div style={{ fontSize: '32px', fontWeight: '700', color: '#7c3aed' }}>{analytics.leadsThisMonth}</div>
              <div style={{ fontSize: '14px', color: '#6b7280' }}>Este Mes</div>
            </div>
          </div>

          {/* Charts Row */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '2fr 1fr',
            gap: '20px',
            marginBottom: '24px'
          }}>
            {/* Leads by Day Chart */}
            <div style={{
              background: 'white',
              border: '2px solid #e5e7eb',
              borderRadius: '12px',
              padding: '20px'
            }}>
              <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', color: '#1e3a5f', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <TrendingUp size={18} /> Leads Últimos 7 Días
              </h3>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px', height: '120px' }}>
                {analytics.leadsByDay.map((day, i) => (
                  <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <div style={{
                      width: '100%',
                      height: `${(day.count / maxDayCount) * 100}px`,
                      background: day.count > 0 ? 'linear-gradient(180deg, #1e3a5f 0%, #3b82f6 100%)' : '#e5e7eb',
                      borderRadius: '4px 4px 0 0',
                      minHeight: '4px',
                      transition: 'height 0.3s ease'
                    }} />
                    <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '6px', textAlign: 'center' }}>
                      {day.day}
                    </div>
                    <div style={{ fontSize: '13px', fontWeight: '600', color: '#1e3a5f' }}>
                      {day.count}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Value Stats */}
            <div style={{
              background: 'white',
              border: '2px solid #e5e7eb',
              borderRadius: '12px',
              padding: '20px'
            }}>
              <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', color: '#1e3a5f', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <DollarSign size={18} /> Valor Estimado
              </h3>
              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontSize: '28px', fontWeight: '700', color: '#059669' }}>
                  ${analytics.totalValue.toLocaleString('en-US')}
                </div>
                <div style={{ fontSize: '13px', color: '#6b7280' }}>Valor total USD</div>
              </div>
              <div style={{ paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
                <div style={{ fontSize: '20px', fontWeight: '600', color: '#1e3a5f' }}>
                  ${analytics.avgValue.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                </div>
                <div style={{ fontSize: '13px', color: '#6b7280' }}>Promedio por lead</div>
              </div>
            </div>
          </div>

          {/* Bottom Row */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '20px'
          }}>
            {/* Top Tariff Codes */}
            <div style={{
              background: 'white',
              border: '2px solid #e5e7eb',
              borderRadius: '12px',
              padding: '20px'
            }}>
              <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', color: '#1e3a5f', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FileText size={18} /> Capítulos Más Buscados
              </h3>
              {analytics.topTariffs.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {analytics.topTariffs.map(([code, count], i) => (
                    <div key={code} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px'
                    }}>
                      <div style={{
                        width: '28px',
                        height: '28px',
                        borderRadius: '6px',
                        background: i === 0 ? '#1e3a5f' : '#e5e7eb',
                        color: i === 0 ? 'white' : '#4b5563',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}>
                        {i + 1}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontFamily: 'monospace', fontWeight: '600', color: '#1e3a5f' }}>{code}...</div>
                      </div>
                      <div style={{
                        background: '#f0fdf4',
                        color: '#059669',
                        padding: '4px 10px',
                        borderRadius: '12px',
                        fontSize: '13px',
                        fontWeight: '600'
                      }}>
                        {count} leads
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ color: '#9ca3af', fontSize: '14px' }}>Sin datos de fracciones aún</p>
              )}
            </div>

            {/* Conversion Stats */}
            <div style={{
              background: 'white',
              border: '2px solid #e5e7eb',
              borderRadius: '12px',
              padding: '20px'
            }}>
              <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', color: '#1e3a5f', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Target size={18} /> Calidad de Leads
              </h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '16px' }}>
                <div style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  background: `conic-gradient(#059669 ${analytics.conversionRate}%, #e5e7eb ${analytics.conversionRate}%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <div style={{
                    width: '60px',
                    height: '60px',
                    borderRadius: '50%',
                    background: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: '700',
                    color: '#059669'
                  }}>
                    {analytics.conversionRate}%
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '14px', color: '#6b7280' }}>Leads completos</div>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: '#1e3a5f' }}>
                    {analytics.completeLeads} / {leads.length}
                  </div>
                </div>
              </div>
              <p style={{ fontSize: '13px', color: '#6b7280', margin: 0 }}>
                Leads con nombre, email y teléfono completos
              </p>
            </div>
          </div>
        </>
      ) : (
        <>
          {/* Table View */}
          <div className="leads-controls" style={{ marginBottom: '16px' }}>
            <div className="leads-filters" style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <div className="filter-group">
                <label style={{ fontSize: '13px', color: '#6b7280', marginRight: '8px' }}>Filtrar:</label>
                <select 
                  value={dateFilter} 
                  onChange={(e) => setDateFilter(e.target.value)}
                  data-testid="date-filter"
                  style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #e5e7eb' }}
                >
                  <option value="all">Todos</option>
                  <option value="today">Hoy</option>
                  <option value="week">Última semana</option>
                  <option value="month">Último mes</option>
                </select>
              </div>
              <div className="filter-group search-group" style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                <Search size={16} style={{ color: '#9ca3af' }} />
                <input
                  type="text"
                  placeholder="Buscar por nombre, email o teléfono..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  data-testid="leads-search"
                  style={{ flex: 1, padding: '8px 12px', borderRadius: '6px', border: '1px solid #e5e7eb' }}
                />
              </div>
            </div>
          </div>

          <div style={{ background: '#f8fafc', padding: '12px 16px', borderRadius: '8px', marginBottom: '16px' }}>
            <span style={{ fontWeight: '600', color: '#1e3a5f' }}>{filteredLeads.length}</span>
            <span style={{ color: '#6b7280' }}> leads encontrados</span>
          </div>

          {filteredLeads.length === 0 ? (
            <div className="leads-empty" style={{ textAlign: 'center', padding: '48px', color: '#9ca3af' }}>
              <User size={48} />
              <h3>No hay leads</h3>
              <p>Los leads aparecerán aquí cuando los clientes completen sus datos en la calculadora.</p>
            </div>
          ) : (
            <div className="leads-table-container" style={{ overflowX: 'auto' }}>
              <table className="leads-table" data-testid="leads-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#4b5563', borderBottom: '2px solid #e5e7eb' }}>Nombre</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#4b5563', borderBottom: '2px solid #e5e7eb' }}>Email</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#4b5563', borderBottom: '2px solid #e5e7eb' }}>Teléfono</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#4b5563', borderBottom: '2px solid #e5e7eb' }}>Empresa</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#4b5563', borderBottom: '2px solid #e5e7eb' }}>Fracción</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#4b5563', borderBottom: '2px solid #e5e7eb' }}>Valor USD</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#4b5563', borderBottom: '2px solid #e5e7eb' }}>Fecha</th>
                    <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600', color: '#4b5563', borderBottom: '2px solid #e5e7eb' }}>Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLeads.map((lead) => (
                    <tr key={lead.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '12px', fontWeight: '500' }}>{lead.name || "-"}</td>
                      <td style={{ padding: '12px' }}>
                        {lead.email ? (
                          <a href={`mailto:${lead.email}`} style={{ color: '#3b82f6', textDecoration: 'none' }}>
                            {lead.email}
                          </a>
                        ) : "-"}
                      </td>
                      <td style={{ padding: '12px' }}>
                        {lead.phone ? (
                          <a href={`tel:${lead.phone}`} style={{ color: '#1e3a5f', textDecoration: 'none' }}>
                            {lead.phone}
                          </a>
                        ) : "-"}
                      </td>
                      <td style={{ padding: '12px', color: '#6b7280' }}>{lead.company || "-"}</td>
                      <td style={{ padding: '12px', fontFamily: 'monospace', fontSize: '13px' }}>{lead.tariff || "-"}</td>
                      <td style={{ padding: '12px', fontWeight: '600', color: '#059669' }}>
                        {lead.productValue ? `$${parseInt(lead.productValue).toLocaleString()}` : "-"}
                      </td>
                      <td style={{ padding: '12px', fontSize: '13px', color: '#6b7280' }}>
                        {new Date(lead.created_at).toLocaleDateString("es-MX", {
                          day: "2-digit",
                          month: "short",
                          hour: "2-digit",
                          minute: "2-digit"
                        })}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        <a 
                          href={`https://wa.me/${lead.phone?.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(`Hola ${lead.name}, gracias por usar nuestra calculadora de costos de importación.`)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '32px',
                            height: '32px',
                            background: '#25D366',
                            color: 'white',
                            borderRadius: '6px'
                          }}
                        >
                          <MessageCircle size={16} />
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// Customer Info Form Component
const CustomerInfoForm = ({ customerInfo, setCustomerInfo }) => {
  const handleChange = (field, value) => {
    setCustomerInfo(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="customer-info-section" data-testid="customer-info-section">
      <h3><User size={20} /> Información de Contacto</h3>
      <p className="section-description">Complete sus datos para recibir asesoría personalizada</p>
      <div className="customer-form-grid">
        <div className="form-group">
          <label><User size={14} /> Nombre Completo</label>
          <input
            type="text"
            data-testid="input-customer-name"
            value={customerInfo.name}
            onChange={(e) => handleChange("name", e.target.value)}
            placeholder="Ej: Juan Pérez"
          />
        </div>
        <div className="form-group">
          <label><Mail size={14} /> Correo Electrónico</label>
          <input
            type="email"
            data-testid="input-customer-email"
            value={customerInfo.email}
            onChange={(e) => handleChange("email", e.target.value)}
            placeholder="Ej: juan@empresa.com"
          />
        </div>
        <div className="form-group">
          <label><Phone size={14} /> Teléfono</label>
          <input
            type="tel"
            data-testid="input-customer-phone"
            value={customerInfo.phone}
            onChange={(e) => handleChange("phone", e.target.value)}
            placeholder="Ej: +52 55 1234 5678"
          />
        </div>
      </div>
    </div>
  );
};

// Tab Navigation Component
const TabNavigation = ({ activeTab, setActiveTab, calculationComplete }) => {
  const tabs = [
    { id: "calculator", label: "Calculadora", icon: Calculator, active: true },
    { id: "results", label: "Resultados", icon: BarChart3, active: calculationComplete },
    { id: "scenarios", label: "Escenarios", icon: GitBranch, active: calculationComplete },
    { id: "history", label: "Historial", icon: Clock, active: true },
    { id: "leads", label: "Leads", icon: User, active: true },
    { id: "reference", label: "Referencia", icon: BookOpen, active: true },
  ];

  return (
    <div className="tab-navigation" data-testid="tab-navigation">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          data-testid={`tab-${tab.id}`}
          className={`tab-button ${activeTab === tab.id ? "active" : ""} ${!tab.active ? "disabled" : ""}`}
          onClick={() => tab.active && setActiveTab(tab.id)}
          disabled={!tab.active}
        >
          <tab.icon size={18} />
          <span>{tab.label}</span>
          {tab.active && activeTab === tab.id && <span className="tab-indicator" />}
        </button>
      ))}
      <div className="workflow-indicator">
        <span>Workflow:</span>
        <span className={`status-dot ${calculationComplete ? "complete" : "pending"}`} />
      </div>
    </div>
  );
};

// Calculator Form Component
const CalculatorForm = ({ formData, setFormData, onCalculate, loading, categories, banxicoRate, exchangeRate, setExchangeRate }) => {
  const [tariffSearch, setTariffSearch] = useState("");
  const [tariffResults, setTariffResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showTariffDropdown, setShowTariffDropdown] = useState(false);
  const [selectedTariff, setSelectedTariff] = useState(null);
  const [skipNextSearch, setSkipNextSearch] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(formData.import_costs.product_category);

  // Incoterm options with DMA services
  const incotermOptions = [
    { 
      value: "FOB", 
      label: "FOB (Free on Board)", 
      description: "Vendedor entrega en puerto de origen",
      dmaServices: "DMA International puede cotizar el flete marítimo y el seguro de carga para tu mercancía.",
      ctaMessage: "Hola, tengo una operación FOB y me gustaría cotizar el flete marítimo y seguro con DMA International."
    },
    { 
      value: "CIF", 
      label: "CIF (Cost, Insurance, Freight)", 
      description: "Flete y seguro incluidos hasta puerto destino",
      dmaServices: "DMA International puede encargarse del despacho aduanal, maniobras y flete terrestre en México.",
      ctaMessage: "Hola, tengo una operación CIF y necesito apoyo con el despacho aduanal y logística en México."
    },
    { 
      value: "FCA", 
      label: "FCA (Free Carrier)", 
      description: "Vendedor entrega al transportista designado",
      dmaServices: "DMA International puede gestionar todo el papeleo de exportación, cotizar flete internacional y seguro.",
      ctaMessage: "Hola, tengo una operación FCA y necesito apoyo con exportación, flete y seguro con DMA International."
    },
    { 
      value: "EXW", 
      label: "EXW (Ex Works)", 
      description: "Comprador asume todo desde fábrica",
      dmaServices: "DMA International puede encargarse de TODO: recogida en fábrica, exportación, flete, seguro y despacho en México.",
      ctaMessage: "Hola, tengo una operación EXW y necesito servicio completo de logística con DMA International."
    }
  ];

  const currentIncoterm = formData.import_costs.incoterm || "FOB";
  const currentIncotermData = incotermOptions.find(i => i.value === currentIncoterm);
  const isFreightInsuranceOptional = currentIncoterm === "CIF" || currentIncoterm === "EXW";

  const handleInputChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: parseFloat(value) || 0
      }
    }));
  };

  const handleIncotermChange = (value) => {
    setFormData(prev => ({
      ...prev,
      import_costs: {
        ...prev.import_costs,
        incoterm: value,
        // Si es CIF o EXW, se puede omitir flete y seguro adicional
        international_freight: (value === "CIF" || value === "EXW") ? 0 : prev.import_costs.international_freight
      },
      additional_costs: {
        ...prev.additional_costs,
        insurance: (value === "CIF" || value === "EXW") ? 0 : prev.additional_costs.insurance
      }
    }));
  };

  const handleCategoryChange = (value) => {
    setSelectedCategory(value);
    setFormData(prev => ({
      ...prev,
      import_costs: {
        ...prev.import_costs,
        product_category: value
      }
    }));
  };

  // Debounced tariff search
  const searchTariffs = useCallback(async (query) => {
    if (query.length < 2) {
      setTariffResults([]);
      return;
    }
    
    setSearchLoading(true);
    try {
      const response = await axios.get(`${API}/tariffs/search`, {
        params: { q: query, limit: 20 }
      });
      setTariffResults(response.data.results);
      setShowTariffDropdown(true);
    } catch (error) {
      console.error("Tariff search error:", error);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  useEffect(() => {
    if (skipNextSearch) {
      setSkipNextSearch(false);
      return;
    }
    const timer = setTimeout(() => {
      if (tariffSearch && !selectedTariff) {
        searchTariffs(tariffSearch);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [tariffSearch, searchTariffs, skipNextSearch, selectedTariff]);

  const selectTariff = (tariff) => {
    setSkipNextSearch(true);
    setSelectedTariff(tariff);
    setTariffSearch(tariff.code);
    setShowTariffDropdown(false);
    setTariffResults([]);
    
    // Auto-fill the IGI rate if available
    if (tariff.igi_rate !== null && tariff.igi_rate >= 0) {
      setFormData(prev => ({
        ...prev,
        import_costs: {
          ...prev.import_costs,
          igi_rate: tariff.igi_rate / 100, // Convert percentage to decimal
          fraccion_arancelaria: tariff.code
        }
      }));
    }
  };

  const clearTariff = () => {
    setSelectedTariff(null);
    setTariffSearch("");
    setTariffResults([]);
    setShowTariffDropdown(false);
  };

  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setTariffSearch(value);
    if (selectedTariff && value !== selectedTariff.code) {
      setSelectedTariff(null);
    }
  };

  return (
    <div className="calculator-form" data-testid="calculator-form">
      {/* Hero Section - Fracción Arancelaria as Main Feature */}
      <div 
        className="hero-search-section"
        style={{
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          borderRadius: '20px',
          padding: '48px 40px',
          marginBottom: '24px',
          textAlign: 'center',
          border: 'none'
        }}
      >
        <div className="hero-search-content" style={{ maxWidth: '800px', margin: '0 auto' }}>
          <h2 
            className="hero-search-title"
            style={{
              fontSize: '32px',
              fontWeight: '700',
              color: '#1e3a5f',
              marginBottom: '8px',
              padding: '0',
              border: 'none',
              display: 'block'
            }}
          >
            ¿Qué producto quieres importar?
          </h2>
          <p 
            className="hero-search-subtitle"
            style={{
              fontSize: '17px',
              color: '#6b7280',
              marginBottom: '32px',
              marginTop: '0'
            }}
          >
            Encuentra tu fracción arancelaria y calcula los costos al instante
          </p>
          
          <div className="hero-tariff-search" style={{ maxWidth: '700px', margin: '0 auto', position: 'relative' }}>
            <div 
              className="hero-search-input-wrapper"
              style={{
                display: 'flex',
                alignItems: 'center',
                background: 'white',
                border: '2px solid #d1d5db',
                borderRadius: '50px',
                padding: '8px 24px',
                boxShadow: '0 10px 40px rgba(0,0,0,0.08)'
              }}
            >
              <Search size={24} style={{ color: '#9ca3af', flexShrink: 0 }} />
              <input
                type="text"
                data-testid="input-tariff-search"
                value={tariffSearch}
                onChange={handleSearchInputChange}
                onFocus={() => !selectedTariff && tariffResults.length > 0 && setShowTariffDropdown(true)}
                placeholder="Busca por código o producto... (ej: 8471.30, celulares, textil)"
                style={{
                  flex: 1,
                  border: 'none',
                  outline: 'none',
                  fontSize: '18px',
                  padding: '14px 16px',
                  background: 'transparent',
                  width: 'auto'
                }}
              />
              {searchLoading && <RefreshCw size={20} className="spin" style={{ color: '#d97706' }} />}
              {selectedTariff && (
                <button 
                  onClick={clearTariff} 
                  data-testid="clear-tariff"
                  style={{
                    background: '#f1f5f9',
                    border: 'none',
                    borderRadius: '50%',
                    width: '32px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer'
                  }}
                >
                  <X size={20} />
                </button>
              )}
            </div>
            
            {showTariffDropdown && tariffResults.length > 0 && (
              <div className="hero-tariff-dropdown" data-testid="tariff-dropdown">
                {tariffResults.map((tariff, index) => (
                  <div
                    key={tariff.code}
                    className={`hero-tariff-option ${tariff.is_prohibited ? 'prohibited' : ''} ${tariff.is_exempt ? 'exempt' : ''}`}
                    onClick={() => selectTariff(tariff)}
                    data-testid={`tariff-option-${index}`}
                  >
                    <div className="hero-tariff-main">
                      <span className="hero-tariff-code">{tariff.code}</span>
                      <span className={`hero-tariff-igi ${tariff.is_prohibited ? 'prohibited' : tariff.is_exempt ? 'exempt' : ''}`}>
                        {tariff.is_prohibited ? (
                          <><Ban size={14} /> Prohibida</>
                        ) : tariff.is_exempt ? (
                          <><Check size={14} /> Exento</>
                        ) : tariff.igi_rate !== null ? (
                          `IGI: ${tariff.igi_rate}%`
                        ) : (
                          'N/A'
                        )}
                      </span>
                    </div>
                    <p className="hero-tariff-desc">{tariff.description}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {selectedTariff && (
            <>
              <div className={`hero-selected-tariff ${selectedTariff.is_prohibited ? 'prohibited' : selectedTariff.is_exempt ? 'exempt' : ''}`} data-testid="selected-tariff">
                <CheckCircle size={20} />
                <div className="hero-selected-info">
                  <span className="hero-selected-code">{selectedTariff.code}</span>
                  <span className="hero-selected-desc">{selectedTariff.description?.substring(0, 80)}...</span>
                </div>
                <span className="hero-selected-rate">
                  {selectedTariff.is_exempt ? 'Exento' : `IGI: ${selectedTariff.igi_rate || 0}%`}
                </span>
              </div>
              {/* NOM CTA sutil - mostrar si el producto podría requerir NOM */}
              {(() => {
                const code = selectedTariff.code || "";
                const chapter = parseInt(code.split(".")[0], 10);
                // Capítulos que típicamente requieren NOM: 
                // 84-85: Máquinas/electrónicos, 61-63: Textiles, 04/15-22: Alimentos, 94: Muebles, 95: Juguetes
                const nomChapters = [4, 15, 16, 17, 18, 19, 20, 21, 22, 61, 62, 63, 84, 85, 94, 95];
                const mayRequireNom = nomChapters.includes(chapter);
                
                if (mayRequireNom && !selectedTariff.is_prohibited) {
                  return (
                    <div 
                      className="nom-inline-cta"
                      data-testid="nom-inline-cta"
                      style={{
                        marginTop: '12px',
                        padding: '12px 16px',
                        background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                        borderRadius: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '12px',
                        border: '1px solid #f59e0b'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Shield size={18} style={{ color: '#d97706', flexShrink: 0 }} />
                        <span style={{ fontSize: '13px', color: '#92400e' }}>
                          Este producto podría requerir <strong>certificación NOM</strong>
                        </span>
                      </div>
                      <a 
                        href={`https://wa.me/${WHATSAPP_NUMBER.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(`Hola, quiero verificar si mi producto (${selectedTariff.code}) requiere NOM y cotizar el trámite con DMA International.`)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '6px 12px',
                          background: '#1e3a5f',
                          color: 'white',
                          borderRadius: '6px',
                          fontSize: '12px',
                          fontWeight: '600',
                          textDecoration: 'none',
                          whiteSpace: 'nowrap'
                        }}
                        data-testid="nom-cta-button"
                      >
                        <MessageCircle size={14} />
                        DMA International tramita tu NOM
                      </a>
                    </div>
                  );
                }
                return null;
              })()}
            </>
          )}

          {/* Compact CTAs */}
          <div className="hero-ctas">
            <MiniCTA 
              icon={AlertTriangle}
              text="¿No encuentras tu fracción?"
              ctaText="Clasificación Profesional"
              whatsappMessage="Hola, necesito ayuda para clasificar mi producto arancelariamente."
            />
            <MiniCTA 
              icon={FileText}
              text="¿Tu producto necesita NOM?"
              ctaText="Tramitar NOM"
              whatsappMessage="Hola, necesito información sobre el trámite de certificación NOM."
            />
          </div>
        </div>
      </div>

      {/* Incoterm Cards */}
      <div style={{ marginBottom: '24px' }}>
        <h3 style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '18px', fontWeight: '600', color: '#1e3a5f', marginBottom: '16px' }}>
          <Ship size={20} style={{ color: '#d97706' }} /> ¿Cómo compras tu mercancía?
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
          {incotermOptions.map(opt => (
            <div
              key={opt.value}
              data-testid={`incoterm-${opt.value}`}
              onClick={() => handleIncotermChange(opt.value)}
              style={{
                background: 'white',
                border: currentIncoterm === opt.value ? '2px solid #1e3a5f' : '2px solid #e5e7eb',
                borderRadius: '12px',
                padding: '20px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                textAlign: 'left',
                boxShadow: currentIncoterm === opt.value ? '0 0 0 4px rgba(30, 58, 95, 0.08)' : 'none',
                backgroundColor: currentIncoterm === opt.value ? 'rgba(30, 58, 95, 0.04)' : 'white'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontSize: '20px', fontWeight: '700', color: '#1e3a5f' }}>{opt.value}</span>
                {currentIncoterm === opt.value && <CheckCircle size={18} style={{ color: '#059669' }} />}
              </div>
              <p style={{ fontSize: '13px', color: '#6b7280', margin: '0', lineHeight: '1.4' }}>{opt.description}</p>
              {currentIncoterm === opt.value && (
                <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
                  <span style={{ fontSize: '11px', fontWeight: '600', color: '#d97706', textTransform: 'uppercase', letterSpacing: '0.5px' }}>DMA te ayuda con:</span>
                  <p style={{ fontSize: '12px', color: '#6b7280', margin: '4px 0 12px' }}>{opt.dmaServices?.split('.')[0]}</p>
                  <WhatsAppCTA 
                    message={opt.ctaMessage}
                    buttonText="Cotizar"
                    className="incoterm-card-cta"
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="form-section">
        <h3><Globe size={20} /> Datos de la Importación</h3>

        <div className="form-grid">
          <div className="form-group">
            <label>Valor de Factura Comercial (USD) *</label>
            <input
              type="number"
              data-testid="input-invoice-value"
              value={formData.import_costs.commercial_invoice_value || ""}
              onChange={(e) => handleInputChange("import_costs", "commercial_invoice_value", e.target.value)}
              placeholder="0.00"
            />
          </div>
          <div className="form-group">
            <label>
              Flete Internacional (USD) 
              {!isFreightInsuranceOptional && <span className="required">*</span>}
              {isFreightInsuranceOptional && <span className="optional">(Opcional)</span>}
            </label>
            <input
              type="number"
              data-testid="input-freight"
              value={formData.import_costs.international_freight || ""}
              onChange={(e) => handleInputChange("import_costs", "international_freight", e.target.value)}
              placeholder="0.00"
              disabled={isFreightInsuranceOptional}
              className={isFreightInsuranceOptional ? "disabled-field" : ""}
            />
          </div>
          <div className="form-group exchange-rate-group">
            <label>Tipo de Cambio (USD/MXN) *</label>
            <input
              type="number"
              data-testid="input-exchange-rate"
              value={exchangeRate || ""}
              onChange={(e) => setExchangeRate(parseFloat(e.target.value) || 0)}
              step="0.0001"
            />
            <BanxicoRateInline 
              banxicoRate={banxicoRate}
              onUseRate={() => banxicoRate?.rate && setExchangeRate(banxicoRate.rate)}
            />
            <p className="sat-hint">
              <AlertTriangle size={12} />
              El tipo de cambio lo asignará el SAT el día de la liberación aduanal.
            </p>
          </div>
          <div className="form-group">
            <label>Unidades *</label>
            <input
              type="number"
              data-testid="input-units"
              value={formData.import_costs.units || ""}
              onChange={(e) => handleInputChange("import_costs", "units", e.target.value)}
              min="1"
              placeholder="0"
            />
          </div>
          <div className="form-group category-group">
            <label>Categoría de Producto</label>
            <div className="category-buttons">
              {categories.map(cat => (
                <button
                  key={cat.id}
                  type="button"
                  data-testid={`category-${cat.id}`}
                  className={`category-btn ${selectedCategory === cat.id ? "active" : ""}`}
                  onClick={() => handleCategoryChange(cat.id)}
                >
                  {cat.name_es}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3><Percent size={20} /> Tasas de Impuestos</h3>
        <p className="section-description">Puede modificar estos valores manualmente si tiene datos específicos.</p>
        <div className="form-grid">
          <div className="form-group">
            <label>IGI - Impuesto General de Importación (%)</label>
            <input
              type="number"
              data-testid="input-igi-rate"
              value={formData.import_costs.igi_rate ? (formData.import_costs.igi_rate * 100).toFixed(1) : ""}
              onChange={(e) => handleInputChange("import_costs", "igi_rate", parseFloat(e.target.value) / 100)}
              step="0.1"
              placeholder="0.0"
              className={selectedTariff ? "auto-filled" : ""}
            />
            {selectedTariff && <span className="auto-fill-hint">Auto-llenado desde TIGIE (editable)</span>}
          </div>
          <div className="form-group">
            <label>IGE - Impuesto General de Exportación (%)</label>
            <input
              type="number"
              data-testid="input-ige-rate"
              value={formData.import_costs.ige_rate ? (formData.import_costs.ige_rate * 100).toFixed(1) : ""}
              onChange={(e) => handleInputChange("import_costs", "ige_rate", parseFloat(e.target.value) / 100)}
              step="0.1"
              placeholder="0.0"
            />
          </div>
          <div className="form-group">
            <label>DTA - Derecho de Trámite Aduanero (%)</label>
            <input
              type="number"
              data-testid="input-dta-rate"
              value={(formData.import_costs.dta_rate * 100).toFixed(2)}
              onChange={(e) => handleInputChange("import_costs", "dta_rate", parseFloat(e.target.value) / 100)}
              step="0.01"
              placeholder="0.80"
            />
          </div>
          <div className="form-group">
            <label>IVA (%)</label>
            <input
              type="number"
              data-testid="input-iva-rate"
              value={(formData.import_costs.iva_rate * 100).toFixed(0)}
              onChange={(e) => handleInputChange("import_costs", "iva_rate", parseFloat(e.target.value) / 100)}
              placeholder="16"
            />
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3><Truck size={20} /> Costos Adicionales (MXN)</h3>
        <p className="section-description">Estos servicios aplican IVA (16%) en México. Ingrese montos sin IVA.</p>
        <div className="form-grid costs-grid">
          <div className="form-group">
            <label>Honorarios Agente Aduanal <span className="iva-tag">+IVA</span></label>
            <input
              type="number"
              data-testid="input-broker-fee"
              value={formData.additional_costs.customs_broker_fee || ""}
              onChange={(e) => handleInputChange("additional_costs", "customs_broker_fee", e.target.value)}
              placeholder="0.00"
            />
          </div>
          <div className="form-group">
            <label>Revalidación de BL <span className="iva-tag">+IVA</span></label>
            <input
              type="number"
              data-testid="input-bl-revalidation"
              value={formData.additional_costs.bl_revalidation || ""}
              onChange={(e) => handleInputChange("additional_costs", "bl_revalidation", e.target.value)}
              placeholder="0.00"
            />
          </div>
          <div className="form-group">
            <label>Maniobras Puerto <span className="iva-tag">+IVA</span></label>
            <input
              type="number"
              data-testid="input-handling"
              value={formData.additional_costs.handling || ""}
              onChange={(e) => handleInputChange("additional_costs", "handling", e.target.value)}
              placeholder="0.00"
            />
          </div>
          <div className="form-group">
            <label>Almacenaje (Storage) <span className="iva-tag">+IVA</span></label>
            <input
              type="number"
              data-testid="input-storage"
              value={formData.additional_costs.storage || ""}
              onChange={(e) => handleInputChange("additional_costs", "storage", e.target.value)}
              placeholder="0.00"
            />
          </div>
          <div className="form-group">
            <label>Flete Terrestre (Inland) <span className="iva-tag">+IVA</span></label>
            <input
              type="number"
              data-testid="input-inland-freight"
              value={formData.additional_costs.inland_freight || ""}
              onChange={(e) => handleInputChange("additional_costs", "inland_freight", e.target.value)}
              placeholder="0.00"
            />
          </div>
          <div className="form-group">
            <label>
              Seguro de Carga
              {isFreightInsuranceOptional && <span className="optional">(Incluido en factura)</span>}
            </label>
            <input
              type="number"
              data-testid="input-insurance"
              value={formData.additional_costs.insurance || ""}
              onChange={(e) => handleInputChange("additional_costs", "insurance", e.target.value)}
              placeholder="0.00"
              disabled={isFreightInsuranceOptional}
              className={isFreightInsuranceOptional ? "disabled-field" : ""}
            />
          </div>
          <div className="form-group">
            <label>Gastos Extra 1</label>
            <input
              type="number"
              data-testid="input-extra1"
              value={formData.additional_costs.extra_cost_1 || ""}
              onChange={(e) => handleInputChange("additional_costs", "extra_cost_1", e.target.value)}
              placeholder="0.00"
            />
          </div>
          <div className="form-group">
            <label>Gastos Extra 2</label>
            <input
              type="number"
              data-testid="input-extra2"
              value={formData.additional_costs.extra_cost_2 || ""}
              onChange={(e) => handleInputChange("additional_costs", "extra_cost_2", e.target.value)}
              placeholder="0.00"
            />
          </div>
        </div>

        {/* Mini CTA Flete */}
        <MiniCTA 
          icon={Truck}
          text="¿Necesitas flete terrestre?"
          ctaText="Cotizar"
          whatsappMessage="Hola, necesito cotización de flete terrestre en México."
        />
      </div>

      <div className="form-section compact-section">
        <h3><Target size={20} /> Margen de Ganancia</h3>
        <div className="margin-selector">
          {[0, 0.15, 0.20, 0.25, 0.30, 0.35, 0.40].map(margin => (
            <button
              key={margin}
              data-testid={`margin-${margin * 100}`}
              className={`margin-button ${formData.target_margin === margin ? "active" : ""} ${margin === 0 ? "zero-margin" : ""}`}
              onClick={() => setFormData(prev => ({ ...prev, target_margin: margin }))}
            >
              {margin === 0 ? "0%" : `${(margin * 100).toFixed(0)}%`}
            </button>
          ))}
        </div>
        <p className="margin-hint">0% = Consumo propio</p>
      </div>

      <button
        className="calculate-button"
        data-testid="calculate-btn"
        onClick={onCalculate}
        disabled={loading}
      >
        {loading ? (
          <>
            <RefreshCw size={20} className="spin" />
            Calculando...
          </>
        ) : (
          <>
            <Calculator size={20} />
            Calcular Costo Total
            <ArrowRight size={18} />
          </>
        )}
      </button>
    </div>
  );
};

// Cost Breakdown Component
const CostBreakdown = ({ title, costs, subtotal, expanded, onToggle, icon: Icon }) => {
  const costItems = Object.entries(costs).filter(([key]) => key !== "subtotal");

  return (
    <div className="cost-breakdown" data-testid={`breakdown-${title.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="breakdown-header" onClick={onToggle}>
        <div className="breakdown-title">
          <Icon size={20} className="breakdown-icon" />
          <span>{title}</span>
        </div>
        <div className="breakdown-total">
          <span className="total-amount">{formatCurrency(subtotal)}</span>
          {expanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </div>
      </div>
      {expanded && (
        <div className="breakdown-items">
          {costItems.map(([key, item]) => {
            const IconComponent = iconMap[item.icon] || FileText;
            return (
              <div key={key} className="breakdown-item" data-testid={`cost-item-${key}`}>
                <div className="item-name">
                  <IconComponent size={16} className="item-icon" />
                  <span>{item.name}</span>
                </div>
                <div className="item-values">
                  <span className="item-amount">{formatCurrency(item.amount_mxn)}</span>
                  <span className="item-percentage">{item.percentage}%</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Results Summary Component
const ResultsSummary = ({ result }) => {
  return (
    <div className="results-summary" data-testid="results-summary">
      <div className="summary-header">
        <div className="total-landed-cost">
          <span className="label">Total Landed Cost</span>
          <span className="amount">{formatCurrency(result.total_landed_cost)}</span>
          <span className="per-unit">
            {formatCurrency(result.cost_per_unit)} per unit · {result.units} units
          </span>
        </div>
        <div className="summary-cards">
          <div className="summary-card">
            <FileText size={24} className="card-icon" />
            <span className="card-amount">{formatCurrency(result.invoice_value_mxn)}</span>
            <span className="card-label">Invoice Value</span>
          </div>
          <div className="summary-card">
            <DollarSign size={24} className="card-icon" />
            <span className="card-amount">{formatCurrency(result.total_taxes)}</span>
            <span className="card-label">Total Taxes</span>
          </div>
          <div className="summary-card">
            <Truck size={24} className="card-icon" />
            <span className="card-amount">{formatCurrency(result.total_logistics)}</span>
            <span className="card-label">Logistics</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Financial Viability Component
const FinancialViability = ({ viability }) => {
  return (
    <div className={`financial-viability ${viability.is_viable ? "viable" : "not-viable"}`} data-testid="financial-viability">
      <div className="viability-header">
        {viability.is_viable ? (
          <CheckCircle size={32} className="viability-icon success" />
        ) : (
          <AlertTriangle size={32} className="viability-icon warning" />
        )}
        <div className="viability-title">
          <h3>{viability.is_viable ? "Financially Viable" : "Review Required"}</h3>
          <span className={`viability-badge ${viability.is_viable ? "viable" : "caution"}`}>
            {viability.is_viable ? "Viable" : "Caution"}
          </span>
        </div>
      </div>
      <p className="viability-description">
        {viability.is_viable 
          ? "This import operation shows strong financial viability with healthy margins and acceptable risk levels."
          : "This import operation may need optimization to improve margins and reduce risk."}
      </p>
      <div className="viability-metrics">
        <div className="metric-card">
          <TrendingUp size={24} />
          <span className="metric-value">{viability.projected_margin}%</span>
          <span className="metric-label">Projected Margin</span>
        </div>
        <div className="metric-card">
          <BarChart3 size={24} />
          <span className="metric-value">{viability.roi_estimation}%</span>
          <span className="metric-label">ROI Estimation</span>
        </div>
        <div className="metric-card">
          <Target size={24} />
          <span className="metric-value">{viability.break_even_units}</span>
          <span className="metric-label">Break-even Units</span>
        </div>
        <div className="metric-card">
          <Clock size={24} />
          <span className="metric-value">{viability.payback_period_months} months</span>
          <span className="metric-label">Payback Period</span>
        </div>
      </div>
      <div className="risk-meter">
        <div className="risk-labels">
          <span>Risk Level</span>
          <span>{viability.risk_level}/100</span>
        </div>
        <div className="risk-bar">
          <div 
            className="risk-fill" 
            style={{ width: `${viability.risk_level}%` }}
            data-testid="risk-level-bar"
          />
        </div>
        <div className="risk-scale">
          <span className="low">Low Risk</span>
          <span className="high">High Risk</span>
        </div>
      </div>
    </div>
  );
};

// NOM Requirements Component
const NOMRequirements = ({ requirements, category }) => {
  if (!requirements || requirements.length === 0) {
    return (
      <div className="nom-requirements no-requirements" data-testid="nom-requirements">
        <div className="nom-header">
          <CheckCircle size={32} className="nom-icon success" />
          <div>
            <h3>Sin Certificación NOM Requerida</h3>
            <span className="nom-badge optional">Opcional</span>
          </div>
        </div>
        <p>Esta categoría de producto no requiere certificación NOM obligatoria para el despacho aduanal.</p>
      </div>
    );
  }

  return (
    <div className="nom-requirements" data-testid="nom-requirements">
      <div className="nom-header">
        <AlertTriangle size={32} className="nom-icon warning" />
        <div>
          <h3>Certificación NOM Requerida</h3>
          <span className="nom-badge mandatory">Obligatoria</span>
        </div>
      </div>
      <p>Esta categoría de producto requiere certificación NOM antes del despacho aduanal. Asegúrese de cumplir con los requisitos para evitar retrasos y sanciones.</p>
      <div className="nom-codes">
        {requirements.map((nom, index) => (
          <div key={index} className="nom-code" data-testid={`nom-code-${nom.code}`}>
            <FileText size={16} />
            <span>{nom.code}</span>
          </div>
        ))}
      </div>
      
      {/* CTA NOM Certification */}
      <div className="nom-cta-section">
        <p className="nom-cta-text">¿Necesita tramitar su certificación NOM? Contáctenos para asesoría profesional.</p>
        <WhatsAppCTA 
          message="Hola, me gustaría solicitar información sobre el trámite de certificación NOM para mi producto."
          buttonText="Tramitar NOM"
          className="nom-whatsapp-cta"
        />
      </div>
    </div>
  );
};

// Suggested Pricing Component
const SuggestedPricing = ({ pricing, onRecalculate, currentMargin }) => {
  const [selectedMargin, setSelectedMargin] = useState(currentMargin);

  return (
    <div className="suggested-pricing" data-testid="suggested-pricing">
      <div className="pricing-header">
        <Tag size={32} className="pricing-icon" />
        <h3>Suggested Sales Price</h3>
      </div>
      <div className="price-display">
        <span className="price-label">Recommended Selling Price</span>
        <span className="price-amount">{formatCurrency(pricing.recommended_selling_price)}</span>
        <span className="price-per-unit">per unit</span>
      </div>
      <div className="margin-selection">
        <div className="margin-label">
          <span>Target Margin</span>
          <span className="current-margin">{pricing.target_margin}%</span>
        </div>
        <div className="margin-buttons">
          {[0.15, 0.20, 0.25, 0.30, 0.35, 0.40].map(margin => (
            <button
              key={margin}
              className={`margin-btn ${selectedMargin === margin ? "active" : ""}`}
              onClick={() => {
                setSelectedMargin(margin);
                onRecalculate(margin);
              }}
              data-testid={`pricing-margin-${margin * 100}`}
            >
              {(margin * 100).toFixed(0)}%
            </button>
          ))}
        </div>
      </div>
      <div className="pricing-breakdown">
        <div className="pricing-row">
          <Box size={18} />
          <span>Total Landed Cost</span>
          <span className="value">{formatCurrency(pricing.total_landed_cost)}</span>
        </div>
        <div className="pricing-row profit">
          <TrendingUp size={18} />
          <span>Gross Profit</span>
          <span className="value">{formatCurrency(pricing.gross_profit)}</span>
        </div>
        <div className="pricing-row">
          <BarChart3 size={18} />
          <span>ROI</span>
          <span className="value">{pricing.roi}%</span>
        </div>
      </div>
      <button className="recalculate-btn" onClick={() => onRecalculate(selectedMargin)} data-testid="recalculate-btn">
        <RefreshCw size={18} />
        Recalculate
      </button>
    </div>
  );
};

// History Component
const HistoryTab = ({ history, onLoad, onDelete }) => {
  if (!history || history.length === 0) {
    return (
      <div className="history-empty" data-testid="history-empty">
        <Clock size={48} />
        <h3>No Saved Calculations</h3>
        <p>Your calculation history will appear here once you save calculations.</p>
      </div>
    );
  }

  return (
    <div className="history-list" data-testid="history-list">
      {history.map((item) => (
        <div key={item.id} className="history-item" data-testid={`history-item-${item.id}`}>
          <div className="history-info">
            <h4>{item.name}</h4>
            <span className="history-date">
              {new Date(item.created_at).toLocaleDateString("es-MX", {
                year: "numeric",
                month: "short",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit"
              })}
            </span>
            <span className="history-total">{formatCurrency(item.result.total_landed_cost)}</span>
          </div>
          <div className="history-actions">
            <button onClick={() => onLoad(item)} data-testid={`load-${item.id}`}>Load</button>
            <button onClick={() => onDelete(item.id)} className="delete" data-testid={`delete-${item.id}`}>Delete</button>
          </div>
        </div>
      ))}
    </div>
  );
};

// Reference Tab Component
const ReferenceTab = () => {
  return (
    <div className="reference-tab" data-testid="reference-tab">
      <h2>Import Cost Reference Guide</h2>
      
      <div className="reference-section">
        <h3><Globe size={20} /> Mexican Import Taxes & Duties</h3>
        <div className="reference-table">
          <div className="ref-row header">
            <span>Tax/Duty</span>
            <span>Rate</span>
            <span>Description</span>
          </div>
          <div className="ref-row">
            <span>IVA (VAT)</span>
            <span>16%</span>
            <span>Applied to customs value + duties</span>
          </div>
          <div className="ref-row">
            <span>DTA</span>
            <span>0.8%</span>
            <span>Customs processing fee (min $293 MXN)</span>
          </div>
          <div className="ref-row">
            <span>Arancel</span>
            <span>5-35%</span>
            <span>Varies by product HS code</span>
          </div>
        </div>
      </div>

      <div className="reference-section">
        <h3><FileText size={20} /> Common NOM Certifications</h3>
        <div className="reference-list">
          <div className="ref-item">
            <strong>NOM-001-SEDE</strong> - Electrical installations safety
          </div>
          <div className="ref-item">
            <strong>NOM-016-ENER</strong> - Energy efficiency for appliances
          </div>
          <div className="ref-item">
            <strong>NOM-051-SCFI</strong> - Food labeling requirements
          </div>
          <div className="ref-item">
            <strong>NOM-004-SCFI</strong> - Textile labeling requirements
          </div>
        </div>
      </div>

      <div className="reference-section">
        <h3><Calculator size={20} /> Cost Calculation Formula</h3>
        <div className="formula-box">
          <code>
            CIF Value = Invoice + Freight + Insurance<br/>
            Customs Value = CIF × Exchange Rate<br/>
            Total Duties = Customs Value × (Duty Rate + DTA Rate)<br/>
            IVA = (Customs Value + Duties) × 16%<br/>
            Landed Cost = Customs Value + Duties + IVA + Logistics
          </code>
        </div>
      </div>
    </div>
  );
};

// Main App Component
function App() {
  const [activeTab, setActiveTab] = useState("calculator");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [categories, setCategories] = useState([
    { id: "general", name: "General / Other", name_es: "General / Otros" }
  ]);
  const [expandedSections, setExpandedSections] = useState({
    import: true,
    additional: true
  });

  // Customer Info State
  const [customerInfo, setCustomerInfo] = useState({
    name: "",
    email: "",
    phone: ""
  });

  // Banxico Exchange Rate State
  const [banxicoRate, setBanxicoRate] = useState(null);
  const [exchangeRate, setExchangeRate] = useState(17.5);

  // Leads State
  const [leads, setLeads] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(false);

  const [formData, setFormData] = useState({
    import_costs: {
      commercial_invoice_value: 0,
      international_freight: 0,
      igi_rate: 0,
      ige_rate: 0,
      dta_rate: 0.008,
      iva_rate: 0.16,
      exchange_rate: 17.5,
      units: 0,
      product_category: "general",
      incoterm: "FOB" // FOB, CIF, FCA, EXW
    },
    additional_costs: {
      customs_broker_fee: 0,
      bl_revalidation: 0,
      handling: 0,
      storage: 0,
      inland_freight: 0,
      insurance: 0,
      extra_cost_1: 0,
      extra_cost_2: 0
    },
    target_margin: 0.25
  });

  // Sync exchangeRate with formData
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      import_costs: {
        ...prev.import_costs,
        exchange_rate: exchangeRate
      }
    }));
  }, [exchangeRate]);

  useEffect(() => {
    fetchCategories();
    fetchHistory();
    fetchBanxicoRate();
    fetchLeads();
  }, []);

  const fetchBanxicoRate = async () => {
    try {
      const response = await axios.get(`${API}/banxico/exchange-rate`);
      setBanxicoRate(response.data);
      if (response.data.success && response.data.rate) {
        setExchangeRate(response.data.rate);
      }
    } catch (error) {
      console.error("Error fetching Banxico rate:", error);
    }
  };

  const fetchLeads = async () => {
    setLeadsLoading(true);
    try {
      const response = await axios.get(`${API}/leads`);
      setLeads(response.data);
    } catch (error) {
      console.error("Error fetching leads:", error);
    } finally {
      setLeadsLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/product-categories`);
      setCategories(response.data.categories);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/calculations/history`);
      setHistory(response.data);
    } catch (error) {
      console.error("Error fetching history:", error);
    }
  };

  const handleCalculate = async () => {
    // Validación de campos requeridos con mensajes específicos
    const errors = [];
    
    if (!formData.import_costs.commercial_invoice_value || formData.import_costs.commercial_invoice_value <= 0) {
      errors.push("• Valor de Factura Comercial: Debe ser mayor a 0");
    }
    if (!formData.import_costs.units || formData.import_costs.units <= 0) {
      errors.push("• Unidades: Debe especificar al menos 1 unidad");
    }
    if (!exchangeRate || exchangeRate <= 0) {
      errors.push("• Tipo de Cambio: Debe ser mayor a 0");
    }
    
    // Validaciones específicas por Incoterm
    const incoterm = formData.import_costs.incoterm || "FOB";
    if (incoterm === "FOB" || incoterm === "FCA") {
      if (!formData.import_costs.international_freight || formData.import_costs.international_freight <= 0) {
        errors.push("• Flete Internacional: Requerido para términos " + incoterm);
      }
    }
    
    if (errors.length > 0) {
      alert("Error en los datos de entrada:\n\n" + errors.join("\n") + "\n\nPor favor revise los campos marcados.");
      return;
    }

    // Guardar lead si hay información de contacto
    if (customerInfo.name || customerInfo.email || customerInfo.phone) {
      try {
        await axios.post(`${API}/leads`, customerInfo);
      } catch (err) {
        console.error("Error saving lead:", err);
      }
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/calculate`, formData);
      setResult(response.data);
      setActiveTab("results");
    } catch (error) {
      console.error("Calculation error:", error);
      const errorMsg = error.response?.data?.detail || "Error desconocido en el servidor";
      alert(`Error al calcular costos:\n\n${errorMsg}\n\nPor favor verifique que todos los valores sean válidos.`);
    } finally {
      setLoading(false);
    }
  };

  const handleRecalculateWithMargin = async (newMargin) => {
    if (!result) return;
    
    setLoading(true);
    try {
      const updatedFormData = { ...formData, target_margin: newMargin };
      const response = await axios.post(`${API}/calculate`, updatedFormData);
      setResult(response.data);
      setFormData(updatedFormData);
    } catch (error) {
      console.error("Recalculation error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCalculation = async () => {
    if (!result) return;
    const name = prompt("Enter a name for this calculation:");
    if (!name) return;

    try {
      await axios.post(`${API}/calculations/save?name=${encodeURIComponent(name)}`, result);
      fetchHistory();
      alert("Calculation saved!");
    } catch (error) {
      console.error("Error saving calculation:", error);
    }
  };

  const handleLoadCalculation = (item) => {
    setResult(item.result);
    setActiveTab("results");
  };

  const handleDeleteCalculation = async (id) => {
    if (!window.confirm("Are you sure you want to delete this calculation?")) return;
    try {
      await axios.delete(`${API}/calculations/${id}`);
      fetchHistory();
    } catch (error) {
      console.error("Error deleting calculation:", error);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  return (
    <div className="app" data-testid="app-container">
      {/* SEO Hero Header */}
      <header className="app-header-new">
        <div className="header-content">
          <div className="header-brand">
            <PieChart size={28} />
            <span>DMA International</span>
          </div>
          <div className="header-main">
            <h1>¿Cuánto costará importar tu producto a México?</h1>
            <p>Calcula impuestos, aranceles (IGI), IVA y costos logísticos en segundos</p>
          </div>
        </div>
      </header>

      <TabNavigation 
        activeTab={activeTab} 
        setActiveTab={setActiveTab} 
        calculationComplete={!!result}
      />

      <main className="app-main">
        {activeTab === "calculator" && (
          <div 
            className="calculator-layout"
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 380px',
              gap: '32px',
              maxWidth: '1400px',
              margin: '0 auto',
              padding: '24px'
            }}
          >
            {/* Main Form Area */}
            <div className="calculator-form-area" style={{ minWidth: 0 }}>
              {/* Calculator Form */}
              <CalculatorForm
                formData={formData}
                setFormData={setFormData}
                onCalculate={handleCalculate}
                loading={loading}
                categories={categories}
                banxicoRate={banxicoRate}
                exchangeRate={exchangeRate}
                setExchangeRate={setExchangeRate}
              />
            </div>
            
            {/* Sticky Live Preview Sidebar */}
            <div 
              className="calculator-preview-area"
              style={{
                position: 'sticky',
                top: '24px',
                height: 'fit-content'
              }}
            >
              <LiveCostPreview formData={formData} exchangeRate={exchangeRate} />
              
              {/* Lead capture at the end */}
              <div 
                className="lead-capture-box"
                style={{
                  background: 'white',
                  border: '2px solid #e5e7eb',
                  borderRadius: '16px',
                  padding: '24px',
                  marginTop: '20px'
                }}
              >
                <h4 style={{ fontSize: '16px', color: '#1e3a5f', marginBottom: '4px' }}>¿Quieres el reporte completo?</h4>
                <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '16px' }}>Deja tus datos y te contactamos</p>
                <input
                  type="text"
                  placeholder="Nombre"
                  value={customerInfo.name}
                  onChange={(e) => setCustomerInfo(prev => ({ ...prev, name: e.target.value }))}
                  data-testid="lead-name"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px',
                    marginBottom: '10px'
                  }}
                />
                <input
                  type="email"
                  placeholder="Email"
                  value={customerInfo.email}
                  onChange={(e) => setCustomerInfo(prev => ({ ...prev, email: e.target.value }))}
                  data-testid="lead-email"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px',
                    marginBottom: '10px'
                  }}
                />
                <input
                  type="tel"
                  placeholder="Teléfono"
                  value={customerInfo.phone}
                  onChange={(e) => setCustomerInfo(prev => ({ ...prev, phone: e.target.value }))}
                  data-testid="lead-phone"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {activeTab === "results" && result && (
          <div className="results-tab" data-testid="results-tab">
            <ResultsSummary result={result} />
            
            <div className="cost-breakdowns">
              <CostBreakdown
                title="Import Costs"
                costs={result.import_costs}
                subtotal={result.import_costs.subtotal}
                expanded={expandedSections.import}
                onToggle={() => toggleSection("import")}
                icon={Globe}
              />
              <CostBreakdown
                title="Additional Costs"
                costs={result.additional_costs}
                subtotal={result.additional_costs.subtotal}
                expanded={expandedSections.additional}
                onToggle={() => toggleSection("additional")}
                icon={Truck}
              />
            </div>

            <div className="results-grid">
              <FinancialViability viability={result.financial_viability} />
              <NOMRequirements 
                requirements={result.nom_requirements} 
                category={result.product_category}
              />
              <SuggestedPricing 
                pricing={result.suggested_pricing}
                onRecalculate={handleRecalculateWithMargin}
                currentMargin={formData.target_margin}
              />
            </div>

            <div className="results-actions">
              <button onClick={handleSaveCalculation} className="save-btn" data-testid="save-calculation-btn">
                Save Calculation
              </button>
              <button onClick={() => setActiveTab("calculator")} className="edit-btn" data-testid="edit-calculation-btn">
                Edit Inputs
              </button>
            </div>
          </div>
        )}

        {activeTab === "scenarios" && result && (
          <div className="scenarios-tab" data-testid="scenarios-tab">
            <h2>Scenario Comparison</h2>
            <p>Compare different import scenarios by adjusting variables.</p>
            <div className="scenario-placeholder">
              <GitBranch size={48} />
              <p>Scenario comparison feature coming soon!</p>
              <p>You'll be able to compare different exchange rates, duty rates, and quantities.</p>
            </div>
          </div>
        )}

        {activeTab === "history" && (
          <div className="history-tab" data-testid="history-tab">
            <h2>Historial de Cálculos</h2>
            <HistoryTab 
              history={history}
              onLoad={handleLoadCalculation}
              onDelete={handleDeleteCalculation}
            />
          </div>
        )}

        {activeTab === "leads" && (
          <LeadsDashboard 
            leads={leads}
            onRefresh={fetchLeads}
            loading={leadsLoading}
          />
        )}

        {activeTab === "reference" && <ReferenceTab />}
      </main>

      <footer className="app-footer">
        <p>DMA International - Calculadora de Costos de Importación</p>
      </footer>
    </div>
  );
}

// Export the new LeadCalculator as the main component
// To use the old App, change this export
export default LeadCalculator;

// Keep App available for advanced features
export { App };
